import { cn } from '@cobliteam/ui/utils';
import type { Transition, Variants } from 'motion/react';
import { motion, useAnimation } from 'motion/react';
import type { HTMLAttributes } from 'react';
import { forwardRef, useEffect } from 'react';

export interface TruckIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface TruckIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  isHovered: boolean;
}

const defaultTransition: Transition = {
  duration: 1.8,
  ease: 'easeInOut',
};

const truckVariants: Variants = {
  normal: {
    x: 0,
    opacity: 1,
  },
  animate: {
    x: [0, -1.1, 0, 100],
    opacity: [1, 1, 1, 1],
    transition: {
      ...defaultTransition,
      times: [0, 0.1, 0.2, 1],
    },
  },
};

const secondTruckVariants: Variants = {
  normal: {
    x: -100,
    opacity: 0,
  },
  animate: {
    x: [-100, 0, -1.1, 0],
    opacity: [1, 1, 1, 1],
    transition: {
      ...defaultTransition,
      delay: 0.2,
      times: [0, 0.4, 0.8, 1],
    },
  },
};

const TruckSVG = ({ controls, variants, size }: { controls: any; variants: Variants; size: number | undefined }) => (
  <motion.svg
    data-testid='lucide-truck'
    xmlns='http://www.w3.org/2000/svg'
    width={18}
    height={18}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    variants={variants}
    animate={controls}
    className='absolute'
  >
    <path d='M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2' />
    <path d='M15 18H9' />
    <path d='M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14' />
    <circle cx='17' cy='18' r='2' />
    <circle cx='7' cy='18' r='2' />
  </motion.svg>
);

const TruckIcon = forwardRef<TruckIconHandle, TruckIconProps>(({ onMouseEnter, onMouseLeave, className, size = 28, isHovered, ...props }, ref) => {
  const firstTruckControls = useAnimation();
  const secondTruckControls = useAnimation();

  useEffect(() => {
    if (isHovered) {
      firstTruckControls.start('animate');
      secondTruckControls.start('animate');
    } else {
      firstTruckControls.start('normal');
      secondTruckControls.start('normal');
    }
  }, [isHovered, firstTruckControls, secondTruckControls]);

  return (
    <div className={cn('relative overflow-hidden', className)} style={{ width: size, height: size }} {...props}>
      <TruckSVG controls={firstTruckControls} variants={truckVariants} size={size} />
      <TruckSVG controls={secondTruckControls} variants={secondTruckVariants} size={size} />
    </div>
  );
});

TruckIcon.displayName = 'TruckIcon';

export { TruckIcon };
